import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAudioPlayer } from '@contexts/AudioPlayerContext';
import {
  getEpisodeProgress,
  getEpisodeSummary,
  shouldExpandEpisodeSummary
} from '@models/episodes/episodeModel';
import { getEpisodeDetailRoute } from '@constants/routes';

export function useEpisodeCardController({ episode, onPlayClick }) {
  const navigate = useNavigate();
  const { episodePositions, playingEpisodeUrl } = useAudioPlayer();
  const [expanded, setExpanded] = useState(false);

  const progress = getEpisodeProgress(episode, episodePositions[episode.url]);
  const shouldTruncate = shouldExpandEpisodeSummary(episode.summary);
  const summaryText = getEpisodeSummary(episode.summary, expanded);
  const isPlaying = playingEpisodeUrl === episode.url;

  const onTranscriptClick = useCallback(() => {
    if (episode.hasTranscript) {
      navigate(getEpisodeDetailRoute(episode.id));
    }
  }, [episode.hasTranscript, episode.id, navigate]);

  const onCardClick = useCallback(() => {
    if (episode.hasTranscript) {
      onTranscriptClick();
    }
  }, [episode.hasTranscript, onTranscriptClick]);

  const onCardKeyDown = useCallback((event) => {
    if (
      episode.hasTranscript &&
      event.target === event.currentTarget &&
      (event.key === 'Enter' || event.key === ' ')
    ) {
      event.preventDefault();
      onTranscriptClick();
    }
  }, [episode.hasTranscript, onTranscriptClick]);

  const onToggleSummary = useCallback((event) => {
    event.stopPropagation();
    setExpanded((isExpanded) => !isExpanded);
  }, []);

  const onPlay = useCallback((event) => {
    event.stopPropagation();
    onPlayClick(episode);
  }, [episode, onPlayClick]);

  return {
    episode,
    isPlaying,
    progress,
    summaryText,
    shouldTruncate,
    expanded,
    onTranscriptClick,
    onCardClick,
    onCardKeyDown,
    onToggleSummary,
    onPlay
  };
}
